import {
  getSchemaText,
  initVersionedSchema,
  toTextContent,
} from '@principle-theorem/editor';
import {
  IDestinationEntityJobRunOptions,
  ITranslationMap,
  type FailedDestinationEntityRecord,
  type IDestinationEntity,
  type IDestinationEntityRecord,
  type IGetRecordResponse,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  ChartedCondition,
  ChartedSurface,
  ClinicalChart,
  ClinicalNote,
  ConditionConfiguration,
  ConditionConfigurationName,
  MockAllTeeth,
  getQuadrant,
  getQuadrantIndex,
  stafferToNamedDoc,
} from '@principle-theorem/principle-core';
import {
  IArraySorter,
  PerioDataPoint,
  PerioMeasurement,
  Quadrant,
  isToothSurface,
  type IBrand,
  type IChartedCondition,
  type IChartedSurface,
  type IClinicalChart,
  type IClinicalNote,
  type IConditionConfiguration,
  type IPatient,
  type IPerioRecord,
  type IStaffer,
  type IToothRef,
  type ToothNumber,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  WithPrimaryKey,
  find$,
  getError,
  resolveSequentially,
  snapshot,
  snapshotCombineLatest,
  toISODate,
  toNamedDocument,
  where,
  type DocumentReference,
  type IIdentifiable,
  type INamedDocument,
  type ISODateType,
  type Timestamp,
  type WithRef,
} from '@principle-theorem/shared';
import { compact, first, groupBy, uniqBy } from 'lodash';
import { combineLatest, from, of, type Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { DestinationEntity } from '../../../destination/destination-entity';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import {
  BasePatientClinicalChartDestinationEntity,
  IBaseClinicalChartJobData,
  IClinicalChartMigrationData,
  IGeneratedCharts,
  PATIENT_CLINICAL_CHART_RESOURCE_TYPE,
} from '../../../destination/entities/patient-clinical-charts';
import { STAFFER_RESOURCE_TYPE } from '../../../destination/entities/staff';
import { PatientIdFilter } from '../../../destination/filters/patient-id-filter';
import { PracticeIdFilter } from '../../../destination/filters/practice-id-filter';
import { getPractitionerOrDefaultMapping } from '../../../mappings/staff';
import { PracticeMigration } from '../../../practice-migrations';
import { SourceEntityRecord } from '../../../source/source-entity-record';
import { type TranslationMapHandler } from '../../../translation-map';
import {
  D4W_CONDITION_MAP,
  D4W_CONDITION_PRINCIPLE_MAP,
  type D4WCondition,
} from '../../source/entities/lib/conditions';
import { D4WPatient, PatientSourceEntity } from '../../source/entities/patient';
import {
  D4WPatientChart,
  PatientChartSourceEntity,
} from '../../source/entities/patient-chart';
import {
  D4WPatientMissingTooth,
  PatientMissingToothSourceEntity,
  d4wMissingToothToISONotation,
} from '../../source/entities/patient-missing-teeth';
import {
  D4WPatientPerioChart,
  PATIENT_PERIO_CHART_RESOURCE_TYPE,
  PatientPerioChartSourceEntity,
  type ID4WPatientPerioChartRecord,
} from '../../source/entities/patient-perio-chart';
import {
  D4WPatientPreExistingTreatment,
  PatientPreExistingTreatmentSourceEntity,
  type ID4WPatientPreExistingTreatment,
} from '../../source/entities/patient-pre-existing-treatment';
import {
  D4WPatientToothCondition,
  PatientToothConditionSourceEntity,
  getConditionChartedRef,
} from '../../source/entities/patient-tooth-condition';
import { TOOTH_SURFACES_MAP } from '../../source/entities/patient-treatment';
import { PatientTreatmentNoteSourceEntity } from '../../source/entities/patient-treatment-note';
import { D4WItemCodeToConditionMappingHandler } from '../mappings/item-code-to-condition';
import { D4WItemCodeMappingHandler } from '../mappings/item-codes';
import { D4WStafferMappingHandler } from '../mappings/staff';
import { getChartedRefs } from './patient-treatment-plans';
import { PatientDestinationEntity } from './patients';

export const PATIENT_CLINICAL_CHART_DESTINATION_ENTITY = DestinationEntity.init(
  {
    metadata: {
      key: PATIENT_CLINICAL_CHART_RESOURCE_TYPE,
      label: 'Patient Clinical Charts',
      description: `The conversion will be done as follows:
        Conditions:
        - Conditions are stored on the clinical chart as expected

        Pre-existing treatments:
        - Any treatment in the "Current Condition" section of the D4W chart can't be converted due the nature of how they're tracked.

        Treatments:
        - All treatments, whether performend or not, will be put in a single treatment plan.
        - Any performed treatments will be grouped by their treatment date.
        - Any pending treatments will be combined into a single future treatment step.
        - Any appointments booked for the patient will need to have treatment added to it manually.

        A "latest" chart will be added to include all:
          - Dentition Records
          - Clinical Notes
          - Conditions
          - Latest Periodontal Chart

        This acts as the baseline chart going forward in Principle. The most latest information will be on this chart. The dates for when notes, conditions, and treatments were added will be mirrored.

        Some things to be aware of:
        - Periodontal FGM measurements can't be converted to Principle

        If no practitioner is available for the following, the preferred practitioner for the patient has been used:
          - Conditions
          - Flagged Treatments
          - Clinical Notes
      `,
    },
  }
);

interface IPerioChartData {
  id: string;
  date: Timestamp;
  patientId: string;
  practitionerId: string;
  data: IGetRecordResponse<D4WPatientPerioChart>[];
}

interface IChartData {
  id: string;
  date: Timestamp;
  patientId: string;
  practitionerId?: string;
  data: IGetRecordResponse<D4WPatientChart>[];
}

export interface IClinicalChartJobData
  extends IBaseClinicalChartJobData<D4WPatient> {
  existingTreatmentMappings: WithPrimaryKey<
    ITranslationMap<IConditionConfiguration>
  >[];
}

export class PatientClinicalChartDestinationEntity extends BasePatientClinicalChartDestinationEntity<
  D4WPatient,
  IClinicalChartJobData
> {
  periodontalResourceType = PATIENT_PERIO_CHART_RESOURCE_TYPE;
  destinationEntity = PATIENT_CLINICAL_CHART_DESTINATION_ENTITY;

  override filters = [
    new PracticeIdFilter<IClinicalChartJobData>((jobData) =>
      jobData.sourcePatient.data.practice_id.toString()
    ),
    new PatientIdFilter<IClinicalChartJobData>((jobData) =>
      this.sourceEntities.patients
        .getSourceRecordId(jobData.sourcePatient.data)
        .toString()
    ),
  ];

  override sorters: IArraySorter[] = [
    {
      key: 'clinicalNotes',
      sortByPath: (data) => getSchemaText((data as IClinicalNote).content),
    },
    {
      key: 'conditions',
      sortByPath: ['type', 'config.ref.id', 'scopeRef'],
    },
  ];

  override canMigrateByIdRange = true;

  sourceCountComparison = new PatientSourceEntity();

  override sourceEntities = {
    patients: new PatientSourceEntity(),
    clinicalCharts: new PatientChartSourceEntity(),
    clinicalNotes: new PatientTreatmentNoteSourceEntity(),
    missingTeeth: new PatientMissingToothSourceEntity(),
    periodontalChart: new PatientPerioChartSourceEntity(),
    toothConditions: new PatientToothConditionSourceEntity(),
    preExistingTreatments: new PatientPreExistingTreatmentSourceEntity(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
  };

  customMappings = {
    staff: new D4WStafferMappingHandler(),
    itemCodes: new D4WItemCodeMappingHandler(),
    itemCodeToCondition: new D4WItemCodeToConditionMappingHandler(),
  };

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<IClinicalChartJobData[]> {
    const staff$ = combineLatest([
      this.customMappings.staff.getRecords(translationMap),
      translationMap.getByType<IStaffer>(STAFFER_RESOURCE_TYPE),
    ]).pipe(map(([staff, mappedStaff]) => [...staff, ...mappedStaff]));

    return snapshotCombineLatest([
      staff$,
      from(this.customMappings.itemCodes.getRecords(translationMap)),
      from(this.customMappings.itemCodeToCondition.getRecords(translationMap)),
    ]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.sourceEntities.patients,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(
        ([
          sourcePatients,
          [staff, sourceItemCodes, existingTreatmentMappings],
        ]) =>
          sourcePatients.map((sourcePatient) => ({
            staff,
            sourceItemCodes,
            existingTreatmentMappings,
            sourcePatient,
            destinationEntity,
          }))
      )
    );
  }

  async buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: IClinicalChartJobData
  ): Promise<
    | IClinicalChartMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
  > {
    try {
      const sourcePatientId = data.sourcePatient.data.patient_id.toString();
      const patientRef = await translationMap.getDestination<IPatient>(
        sourcePatientId,
        PATIENT_RESOURCE_TYPE
      );

      if (!patientRef) {
        return this._buildErrorResponse(
          data.sourcePatient.record,
          `Couldn't resolve patient`
        );
      }

      const providerId = data.sourcePatient.data.provider_id?.toString();
      const practitionerMap = getPractitionerOrDefaultMapping(
        providerId,
        data.staff
      )?.destinationIdentifier;

      if (!practitionerMap) {
        return this._buildErrorResponse(
          data.sourcePatient.record,
          `Couldn't resolve practitioner ${
            data.sourcePatient.data.provider_id ?? ''
          }`
        );
      }

      const charts = await this._buildClinicalChartData(
        migration,
        practitionerMap,
        sourcePatientId,
        data.staff,
        translationMap,
        data
      );

      return {
        sourcePatientId,
        patientRef,
        charts,
      };
    } catch (error) {
      return this._buildErrorResponse(
        data.sourcePatient.record,
        `Failed to build clinical chart data: ${getError(error)}`
      );
    }
  }

  private async _buildClinicalChartData(
    migration: WithRef<IPracticeMigration>,
    practitionerRef: DocumentReference<IStaffer>,
    patientUid: string,
    staff: WithPrimaryKey<ITranslationMap<IStaffer>>[],
    translationMap: TranslationMapHandler,
    data: IClinicalChartJobData
  ): Promise<IGeneratedCharts> {
    const sourcePerioChartItems =
      await this.sourceEntities.periodontalChart.filterRecords(
        migration,
        'patientId',
        patientUid
      );

    const perioClinicalCharts = await buildPerioCharts(
      sourcePerioChartItems,
      staff,
      translationMap
    );

    const conditions = await this.sourceEntities.toothConditions.filterRecords(
      migration,
      'patientId',
      patientUid
    );

    const preExistingTreatments =
      await this.sourceEntities.preExistingTreatments.filterRecords(
        migration,
        'patientId',
        patientUid
      );

    const missingTeeth = await this.sourceEntities.missingTeeth.filterRecords(
      migration,
      'patientId',
      patientUid
    );

    const brand = await snapshot(PracticeMigration.brand$(migration));
    const practitioner = stafferToNamedDoc(
      await Firestore.getDoc(practitionerRef)
    );

    const sourceCharts = await this.sourceEntities.clinicalCharts.filterRecords(
      migration,
      'patientId',
      patientUid
    );

    const clinicalCharts = await buildClinicalCharts(
      data,
      practitioner,
      migration,
      brand,
      sourceCharts,
      conditions,
      missingTeeth,
      preExistingTreatments,
      staff,
      translationMap
    );

    return {
      clinicalCharts,
      perioCharts: compact(perioClinicalCharts),
    };
  }
}

async function buildClinicalCharts(
  data: IClinicalChartJobData,
  defaultPractitioner: INamedDocument<IStaffer>,
  migration: WithRef<IPracticeMigration>,
  brand: WithRef<IBrand>,
  sourceCharts: IGetRecordResponse<D4WPatientChart>[],
  sourceConditions: IGetRecordResponse<D4WPatientToothCondition>[],
  missingTeeth: IGetRecordResponse<D4WPatientMissingTooth>[],
  preExistingTreatments: IGetRecordResponse<D4WPatientPreExistingTreatment>[],
  staff: WithPrimaryKey<ITranslationMap<IStaffer>>[],
  translationMap: TranslationMapHandler
): Promise<(IClinicalChart & IIdentifiable)[]> {
  const charts: IChartData[] = [];

  sourceCharts.map((sourceChart) => {
    const chartFound = charts.find(
      (chart) => chart.id === sourceChart.data.id.toString()
    );
    if (chartFound) {
      chartFound.data.push(sourceChart);
      return;
    }
    charts.push({
      id: sourceChart.data.id.toString(),
      date: sourceChart.record.translations.createdAt,
      patientId: SourceEntityRecord.filterAccessor(
        sourceChart.record,
        'patientId'
      ),
      practitionerId: SourceEntityRecord.filterAccessor(
        sourceChart.record,
        'providerId'
      ),
      data: [sourceChart],
    });
  });

  return resolveSequentially(charts, async (chart) => {
    const createdAt = chart.date;
    const recordDate = toISODate(createdAt, migration.configuration.timezone);
    const createdBy = stafferToNamedDoc(
      chart.practitionerId
        ? await getPractitioner(chart.practitionerId, staff, translationMap)
        : defaultPractitioner
    );

    const conditions = [
      ...(await buildMissingTeeth(
        missingTeeth.filter(
          (missingTooth) => missingTooth.data.chart_id?.toString() === chart.id
        ),
        createdBy,
        brand
      )),
      ...(await buildConditions(
        sourceConditions.filter(
          (sourceCondition) =>
            sourceCondition.data.chart_id?.toString() === chart.id
        ),
        createdBy,
        brand,
        createdAt,
        recordDate
      )),
      ...(await buildPreExistingTreatments(
        preExistingTreatments.filter(
          (sourcePreExistingTreatment) =>
            sourcePreExistingTreatment.data.chart_id?.toString() === chart.id
        ),
        createdBy,
        brand,
        data.existingTreatmentMappings,
        createdAt,
        recordDate
      )),
    ];

    return {
      ...ClinicalChart.init({
        teeth: MockAllTeeth(),
        conditions:
          BasePatientClinicalChartDestinationEntity.mergeMissingToothConditions(
            conditions
          ),
        createdAt,
        createdBy,
        immutable: true,
      }),
      uid: chart.id,
    };
  });
}

async function buildPerioCharts(
  sourcePerioChartItems: IGetRecordResponse<D4WPatientPerioChart>[],
  staff: WithPrimaryKey<ITranslationMap<IStaffer>>[],
  translationMap: TranslationMapHandler
): Promise<(IClinicalChart & IIdentifiable)[]> {
  const perioCharts: IPerioChartData[] = [];

  sourcePerioChartItems.map((sourcePerioChartItem) => {
    const perioChartFound = perioCharts.find(
      (perioChart) => perioChart.id === sourcePerioChartItem.data.id.toString()
    );
    if (perioChartFound) {
      perioChartFound.data.push(sourcePerioChartItem);
      return;
    }
    perioCharts.push({
      id: sourcePerioChartItem.data.id.toString(),
      date: sourcePerioChartItem.record.translations.createdAt,
      patientId: SourceEntityRecord.filterAccessor(
        sourcePerioChartItem.record,
        'patientId'
      ),
      practitionerId: SourceEntityRecord.filterAccessor(
        sourcePerioChartItem.record,
        'providerId'
      ),
      data: [sourcePerioChartItem],
    });
  });

  return resolveSequentially(perioCharts, async (perioChart) => {
    const createdAt = perioChart.date;
    const perioRecords: IPerioRecord[] = compact(
      perioChart.data
        .map((perioChartData) => {
          const groupedRecords = groupBy(
            perioChartData.data.records,
            (record) =>
              record.is_upper_jaw
                ? record.tooth_row_index_number
                : record.tooth_row_index_number + 16
          );
          return Object.entries(groupedRecords).map(([toothNumber, records]) =>
            buildPerioRecord(toothNumber as ToothNumber, records)
          );
        })
        .flat()
    );

    const createdBy = await getPractitioner(
      perioChart.practitionerId,
      staff,
      translationMap
    );

    return {
      ...ClinicalChart.init({
        perioRecords,
        teeth: MockAllTeeth(),
        createdAt,
        createdBy,
        immutable: true,
      }),
      uid: perioChart.id,
    };
  });
}

async function getPractitioner(
  practitionerId: string,
  staff: WithPrimaryKey<ITranslationMap<IStaffer>>[],
  translationMap: TranslationMapHandler
): Promise<INamedDocument<IStaffer>> {
  const practitionerMap =
    staff.find((staffer) => staffer.sourceIdentifier === practitionerId)
      ?.destinationIdentifier ||
    ((await translationMap.getBySource(practitionerId, STAFFER_RESOURCE_TYPE))
      ?.destinationIdentifier as DocumentReference<IStaffer> | undefined);

  if (!practitionerMap) {
    throw new Error(`Couldn't find practitioner with id ${practitionerId}`);
  }

  const creator = await Firestore.getDoc(practitionerMap);
  return stafferToNamedDoc(creator);
}

async function buildMissingTeeth(
  missingTeeth: IGetRecordResponse<D4WPatientMissingTooth>[],
  practitioner: INamedDocument<IStaffer>,
  brand: WithRef<IBrand>
): Promise<IChartedCondition[]> {
  const missingToothCondition = await snapshot(
    find$(
      ConditionConfiguration.col(brand),
      where('name', '==', ConditionConfigurationName.MissingTooth)
    )
  );

  if (!missingToothCondition) {
    throw new Error(`Missing tooth condition is missing`);
  }

  return compact(
    uniqBy(missingTeeth, (missingToothRecord) => {
      const missingTooth = missingToothRecord.data;
      const uniqueAttributes = [
        missingTooth.tooth_number,
        missingTooth.is_child,
      ];
      return uniqueAttributes.join('');
    }).map((missingToothRecord) => {
      const chartedAt = missingToothRecord.record.translations.createdAt;
      const missingTooth = missingToothRecord.data;
      return {
        ...ChartedCondition.fromConfig(missingToothCondition),
        chartedSurfaces: missingTooth.tooth_number
          ? getChartedRefs(
              d4wMissingToothToISONotation(
                missingTooth.tooth_number.toString()
              ),
              [],
              missingTooth.is_child
            ).map((chartedRef) =>
              ChartedSurface.init({
                chartedBy: practitioner,
                chartedAt,
                chartedRef,
              })
            )
          : [],
      };
    })
  );
}

async function buildConditions(
  conditions: IGetRecordResponse<D4WPatientToothCondition>[],
  practitioner: INamedDocument<IStaffer>,
  brand: WithRef<IBrand>,
  chartedAt: Timestamp,
  recordDate: ISODateType
): Promise<IChartedCondition[]> {
  const sourceGroupedConditions = groupBy(
    conditions,
    (condition) =>
      D4W_CONDITION_PRINCIPLE_MAP[
        condition.data.condition_type_id as D4WCondition
      ] ?? ConditionConfigurationName.Other
  );

  const builtConditions = await resolveSequentially(
    Object.values(sourceGroupedConditions),
    async (conditionRecords) => {
      const conditionRecord = first(conditionRecords);
      if (!conditionRecord) {
        return;
      }

      const conditionTypeId = conditionRecord.data
        .condition_type_id as D4WCondition;
      const resolvedCondition = D4W_CONDITION_PRINCIPLE_MAP[conditionTypeId];

      const principleCondition = await snapshot(
        find$(
          ConditionConfiguration.col(brand),
          where(
            'name',
            '==',
            resolvedCondition ?? ConditionConfigurationName.Other
          )
        )
      );

      if (!principleCondition) {
        throw new Error(
          `No condition could be resolved for "${D4W_CONDITION_MAP[conditionTypeId]}"`
        );
      }

      const noChartedSurfaces = conditionRecords.every(
        (condition) => !condition.data.tooth_number
      );
      const noNotes = conditionRecords.every(
        (condition) => !condition.data.notes?.trim()
      );

      if (noChartedSurfaces && noNotes) {
        return;
      }

      const chartedSurfaces = conditionRecords.flatMap((condition) =>
        getConditionChartedRef(
          condition.data.tooth_number,
          condition.data.tooth_surface ?? undefined,
          condition.data.is_baby_tooth
        ).map((chartedRef) =>
          ChartedSurface.init({
            chartedBy: practitioner,
            chartedAt,
            chartedRef,
          })
        )
      );

      const notes = uniqBy(
        conditionRecords,
        (condition) => condition.data.notes?.trim()
      )
        .filter((condition) => !!condition.data.notes?.trim())
        .map((condition) =>
          ClinicalNote.init({
            owner: practitioner,
            content: initVersionedSchema(
              compact([
                toTextContent(D4W_CONDITION_MAP[conditionTypeId]),
                condition.data.notes
                  ? toTextContent(condition.data.notes)
                  : undefined,
              ])
            ),
            createdAt: chartedAt,
            recordDate,
          })
        );

      return ChartedCondition.init({
        config: toNamedDocument(principleCondition),
        chartedSurfaces,
        notes,
      });
    }
  );

  return compact(builtConditions);
}

async function buildPreExistingTreatments(
  preExistingTreatments: IGetRecordResponse<D4WPatientPreExistingTreatment>[],
  practitioner: INamedDocument<IStaffer>,
  brand: WithRef<IBrand>,
  existingTreatmentMappings: WithPrimaryKey<
    ITranslationMap<IConditionConfiguration>
  >[],
  chartedAt: Timestamp,
  recordDate: ISODateType
): Promise<IChartedCondition[]> {
  const sourceGroupedTreatments: Record<
    string,
    {
      mappedTreatment:
        | WithPrimaryKey<ITranslationMap<IConditionConfiguration>>
        | undefined;
      treatmentRecords: IGetRecordResponse<D4WPatientPreExistingTreatment>[];
    }
  > = {
    [ConditionConfigurationName.Other]: {
      mappedTreatment: undefined,
      treatmentRecords: [],
    },
  };

  preExistingTreatments.forEach((treatmentRecord) => {
    const itemCodeId = treatmentRecord.data.item_id.toString();
    const mappedTreatment = existingTreatmentMappings.find(
      (mapping) => mapping.sourceIdentifier === itemCodeId
    );
    if (!mappedTreatment || !mappedTreatment.destinationIdentifier) {
      sourceGroupedTreatments[
        ConditionConfigurationName.Other
      ].treatmentRecords.push(treatmentRecord);
      return;
    }

    const mappedTreatmentPath = mappedTreatment.destinationIdentifier.path;
    if (!sourceGroupedTreatments[mappedTreatmentPath]) {
      sourceGroupedTreatments[mappedTreatmentPath] = {
        mappedTreatment,
        treatmentRecords: [],
      };
    }
    sourceGroupedTreatments[mappedTreatmentPath].treatmentRecords.push(
      treatmentRecord
    );
  });

  const builtTreatments = await resolveSequentially(
    Object.values(sourceGroupedTreatments),
    async ({ mappedTreatment, treatmentRecords }) => {
      const treatmentRecord = first(treatmentRecords);
      if (!treatmentRecord) {
        return;
      }

      const conditionConfiguration = mappedTreatment?.destinationIdentifier
        ? await Firestore.getDoc(mappedTreatment?.destinationIdentifier)
        : await snapshot(
            find$(
              ConditionConfiguration.col(brand),
              where('name', '==', ConditionConfigurationName.Other)
            )
          );

      if (!conditionConfiguration) {
        throw new Error(
          `No condition could be resolved for pre-existing treatments`
        );
      }

      const noChartedSurfaces = treatmentRecords.every(
        (treatment) => !treatment.data.tooth_ref
      );
      const noNotes = treatmentRecords.every(
        (treatment) => !treatment.data.description?.trim()
      );

      if (noChartedSurfaces && noNotes) {
        return;
      }

      const chartedSurfaces = treatmentRecords.flatMap((treatment) =>
        buildChartedSurfaces(treatment.data, practitioner, chartedAt)
      );

      const notes = uniqBy(
        treatmentRecords,
        (treatment) => treatment.data.description?.trim()
      )
        .filter((treatment) => !!treatment.data.description?.trim())
        .map((treatment) =>
          ClinicalNote.init({
            owner: practitioner,
            content: initVersionedSchema([
              toTextContent(
                `${treatment.data.item_code} - ${treatment.data.description}`
              ),
            ]),
            createdAt: chartedAt,
            recordDate,
          })
        );

      return ChartedCondition.init({
        config: toNamedDocument(conditionConfiguration),
        chartedSurfaces,
        notes,
      });
    }
  );

  return compact(builtTreatments);
}

function buildChartedSurfaces(
  treatment: ID4WPatientPreExistingTreatment,
  practitioner: INamedDocument<IStaffer>,
  chartedAt: Timestamp
): IChartedSurface[] {
  if (!treatment.tooth_surface.length) {
    return getConditionChartedRef(
      treatment.tooth_ref,
      undefined,
      treatment.is_baby_tooth
    ).map((chartedRef) =>
      ChartedSurface.init({
        chartedBy: practitioner,
        chartedAt,
        chartedRef,
      })
    );
  }

  return compact(
    treatment.tooth_surface
      .map((toothSurface) => {
        const translatedSurface = TOOTH_SURFACES_MAP[toothSurface];
        if (translatedSurface && !isToothSurface(translatedSurface)) {
          return;
        }
        return getConditionChartedRef(
          treatment.tooth_ref,
          translatedSurface,
          treatment.is_baby_tooth
        ).map((chartedRef) =>
          ChartedSurface.init({
            chartedBy: practitioner,
            chartedAt,
            chartedRef,
          })
        );
      })
      .flat()
  );
}

function buildPerioRecord(
  toothNumber: ToothNumber,
  records: ID4WPatientPerioChartRecord[]
): IPerioRecord | undefined {
  const toothRef = buildToothRef(
    d4wMissingToothToISONotation(toothNumber.toString())
  );

  if (!toothRef) {
    return;
  }

  return {
    toothRef,
    data: {
      [PerioMeasurement.Mobility]: 0,
      [PerioMeasurement.Recession]: {
        facialMesial: determinePerioValue(
          records,
          PerioMeasurement.Recession,
          PerioDataPoint.FacialMesial,
          toothRef
        ),
        facialCentral: determinePerioValue(
          records,
          PerioMeasurement.Recession,
          PerioDataPoint.FacialCentral,
          toothRef
        ),
        facialDistal: determinePerioValue(
          records,
          PerioMeasurement.Recession,
          PerioDataPoint.FacialDistal,
          toothRef
        ),
        palatalMesial: determinePerioValue(
          records,
          PerioMeasurement.Recession,
          PerioDataPoint.PalatalMesial,
          toothRef
        ),
        palatalCentral: determinePerioValue(
          records,
          PerioMeasurement.Recession,
          PerioDataPoint.PalatalCentral,
          toothRef
        ),
        palatalDistal: determinePerioValue(
          records,
          PerioMeasurement.Recession,
          PerioDataPoint.PalatalDistal,
          toothRef
        ),
      },
      [PerioMeasurement.Pocket]: {
        facialMesial: determinePerioValue(
          records,
          PerioMeasurement.Pocket,
          PerioDataPoint.FacialMesial,
          toothRef
        ),
        facialCentral: determinePerioValue(
          records,
          PerioMeasurement.Pocket,
          PerioDataPoint.FacialCentral,
          toothRef
        ),
        facialDistal: determinePerioValue(
          records,
          PerioMeasurement.Pocket,
          PerioDataPoint.FacialDistal,
          toothRef
        ),
        palatalMesial: determinePerioValue(
          records,
          PerioMeasurement.Pocket,
          PerioDataPoint.PalatalMesial,
          toothRef
        ),
        palatalCentral: determinePerioValue(
          records,
          PerioMeasurement.Pocket,
          PerioDataPoint.PalatalCentral,
          toothRef
        ),
        palatalDistal: determinePerioValue(
          records,
          PerioMeasurement.Pocket,
          PerioDataPoint.PalatalDistal,
          toothRef
        ),
      },
      [PerioMeasurement.Bleeding]: {
        facialMesial: determinePerioValue(
          records,
          PerioMeasurement.Bleeding,
          PerioDataPoint.FacialMesial,
          toothRef
        ),
        facialCentral: determinePerioValue(
          records,
          PerioMeasurement.Bleeding,
          PerioDataPoint.FacialCentral,
          toothRef
        ),
        facialDistal: determinePerioValue(
          records,
          PerioMeasurement.Bleeding,
          PerioDataPoint.FacialDistal,
          toothRef
        ),
        palatalMesial: determinePerioValue(
          records,
          PerioMeasurement.Bleeding,
          PerioDataPoint.PalatalMesial,
          toothRef
        ),
        palatalCentral: determinePerioValue(
          records,
          PerioMeasurement.Bleeding,
          PerioDataPoint.PalatalCentral,
          toothRef
        ),
        palatalDistal: determinePerioValue(
          records,
          PerioMeasurement.Bleeding,
          PerioDataPoint.PalatalDistal,
          toothRef
        ),
      },
      [PerioMeasurement.Furcation]: {
        facialMesial: determinePerioValue(
          records,
          PerioMeasurement.Furcation,
          PerioDataPoint.FacialMesial,
          toothRef
        ),
        facialCentral: determinePerioValue(
          records,
          PerioMeasurement.Furcation,
          PerioDataPoint.FacialCentral,
          toothRef
        ),
        facialDistal: determinePerioValue(
          records,
          PerioMeasurement.Furcation,
          PerioDataPoint.FacialDistal,
          toothRef
        ),
        palatalMesial: determinePerioValue(
          records,
          PerioMeasurement.Furcation,
          PerioDataPoint.PalatalMesial,
          toothRef
        ),
        palatalCentral: determinePerioValue(
          records,
          PerioMeasurement.Furcation,
          PerioDataPoint.PalatalCentral,
          toothRef
        ),
        palatalDistal: determinePerioValue(
          records,
          PerioMeasurement.Furcation,
          PerioDataPoint.PalatalDistal,
          toothRef
        ),
      },
      [PerioMeasurement.Suppuration]: {
        facialMesial: determinePerioValue(
          records,
          PerioMeasurement.Suppuration,
          PerioDataPoint.FacialMesial,
          toothRef
        ),
        facialCentral: determinePerioValue(
          records,
          PerioMeasurement.Suppuration,
          PerioDataPoint.FacialCentral,
          toothRef
        ),
        facialDistal: determinePerioValue(
          records,
          PerioMeasurement.Suppuration,
          PerioDataPoint.FacialDistal,
          toothRef
        ),
        palatalMesial: determinePerioValue(
          records,
          PerioMeasurement.Suppuration,
          PerioDataPoint.PalatalMesial,
          toothRef
        ),
        palatalCentral: determinePerioValue(
          records,
          PerioMeasurement.Suppuration,
          PerioDataPoint.PalatalCentral,
          toothRef
        ),
        palatalDistal: determinePerioValue(
          records,
          PerioMeasurement.Suppuration,
          PerioDataPoint.PalatalDistal,
          toothRef
        ),
      },
    },
  };
}

function determinePerioValue(
  records: ID4WPatientPerioChartRecord[],
  measurement: PerioMeasurement,
  measurementPoint: PerioDataPoint,
  toothRef: IToothRef
): number {
  const rightSectionDataPointMap: Record<PerioDataPoint, number> = {
    [PerioDataPoint.FacialDistal]: 1,
    [PerioDataPoint.FacialCentral]: 2,
    [PerioDataPoint.FacialMesial]: 3,
    [PerioDataPoint.PalatalDistal]: 4,
    [PerioDataPoint.PalatalCentral]: 5,
    [PerioDataPoint.PalatalMesial]: 6,
  };

  const leftSectionDataPointMap: Record<PerioDataPoint, number> = {
    [PerioDataPoint.FacialMesial]: 1,
    [PerioDataPoint.FacialCentral]: 2,
    [PerioDataPoint.FacialDistal]: 3,
    [PerioDataPoint.PalatalMesial]: 4,
    [PerioDataPoint.PalatalCentral]: 5,
    [PerioDataPoint.PalatalDistal]: 6,
  };

  const perioFacialMap: Record<PerioMeasurement, number> = {
    [PerioMeasurement.Plaque]: -1,
    [PerioMeasurement.Calculus]: -1,
    [PerioMeasurement.CAL]: -1,
    [PerioMeasurement.Recession]: -1,
    [PerioMeasurement.MGJ]: 2,
    [PerioMeasurement.Pocket]: 3,
    [PerioMeasurement.Furcation]: 4,
    [PerioMeasurement.Mobility]: 5,
    [PerioMeasurement.Bleeding]: 6,
    [PerioMeasurement.Suppuration]: 7,
  };

  const perioPalatalMap: Record<PerioMeasurement, number> = {
    [PerioMeasurement.Plaque]: -1,
    [PerioMeasurement.Calculus]: -1,
    [PerioMeasurement.CAL]: -1,
    [PerioMeasurement.Recession]: -1,
    [PerioMeasurement.Furcation]: -1,
    [PerioMeasurement.Mobility]: -1,
    [PerioMeasurement.MGJ]: 2,
    [PerioMeasurement.Pocket]: 3,
    [PerioMeasurement.Bleeding]: 4,
    [PerioMeasurement.Suppuration]: 5,
  };

  const matchingRecord = records.find((record) => {
    const dataPointMapNumber = record.is_facial
      ? record.sub_column_number
      : record.sub_column_number + 3;

    const dataPointMap = [
      Quadrant.AdultUpperRight,
      Quadrant.AdultLowerRight,
      Quadrant.DeciduousUpperRight,
      Quadrant.DeciduousLowerRight,
    ].includes(toothRef.quadrant)
      ? rightSectionDataPointMap
      : leftSectionDataPointMap;

    const isMatchingDataPoint =
      dataPointMap[measurementPoint] === dataPointMapNumber;

    if (!isMatchingDataPoint) {
      return false;
    }

    const referenceMap = record.is_facial ? perioFacialMap : perioPalatalMap;
    const row = referenceMap[measurement];
    if (row !== record.row_number) {
      return false;
    }

    return true;
  });

  if (!matchingRecord) {
    return 0;
  }

  return matchingRecord.p_value;
}

function buildToothRef(toothNumber: ToothNumber | null): IToothRef | undefined {
  const quadrant = toothNumber ? getQuadrant(toothNumber) : undefined;
  const quadrantIndex = toothNumber ? getQuadrantIndex(toothNumber) : undefined;

  if (!quadrant || !quadrantIndex) {
    return;
  }
  return {
    quadrant,
    quadrantIndex,
  };
}
